import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { UsersRound } from "lucide-react";
import { ApiError } from "@atlas/shared";
import { fetchJoinPreview, joinGroup } from "../../api/groups";
import { clearStoredReferral, storeReferral } from "../../api/referrals";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { usePageMeta } from "../../hooks/usePageMeta";
import { clearPendingGroupJoin, storePendingGroupJoin } from "../../lib/pending-group-join";
import { ErrorLine, GroupImage, fmtNumber, groupErrorMessage } from "./group-ui";

// /groups/join/{code}?ref={username} (spec 32 / ATL-151). Reachable signed
// out (under _publicApp). The preview needs a session, so signed-out
// visitors get a sign-up / sign-in card instead. `ref` is the member who
// shared the link: it goes into the existing invite-a-friend referral
// (localStorage + ?ref= on signup, which also reaches /auth/strava?ref=).
// The code is parked in localStorage so AppShell can bring the visitor back
// here after a Strava sign-up, which always lands on /dashboard.

export const GROUP_CONSENT_COPY =
  "Members will see your collection counts, what you collect and anything you post here. The rest of your profile stays as it is.";

const CARD_STYLE: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-xl)",
  padding: "var(--space-8)",
  boxShadow: "var(--shadow-md), var(--ring-top)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  minWidth: 0,
};

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
      <div style={{ maxWidth: 460, margin: "24px auto", width: "100%" }}>{children}</div>
    </div>
  );
}

function ConsentLine() {
  return (
    <p
      style={{
        fontSize: 13,
        color: "var(--text-secondary)",
        lineHeight: 1.5,
        margin: 0,
        padding: "10px 12px",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {GROUP_CONSENT_COPY}
    </p>
  );
}

const linkButton: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "var(--control-lg)",
  borderRadius: "var(--radius-md)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--text-base)",
  fontWeight: 600,
  textDecoration: "none",
};

function SignedOutCard({ code, inviter }: { code: string; inviter: string | null }) {
  React.useEffect(() => {
    storePendingGroupJoin(code, inviter);
    if (inviter) storeReferral(inviter.toLowerCase());
  }, [code, inviter]);

  // Sign-in is for existing accounts, which earn no referral, so the return
  // path is the bare join page (LoginPage's `next` takes a path, no query).
  const back = `/groups/join/${encodeURIComponent(code)}`;

  return (
    <div style={CARD_STYLE}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <UsersRound size={12} /> Group invite
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
          {inviter ? `${inviter} has asked you into their group` : "You've been asked into a group"}
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "10px 0 0", lineHeight: 1.5 }}>
          Groups on Atlas are private boards and challenges for people who walk together.
          Sign up or sign in, and you'll come straight back here to join.
        </p>
      </div>
      <ConsentLine />
      <Link to="/signup" search={inviter ? { ref: inviter } : {}} style={{ ...linkButton, background: "var(--accent)", color: "var(--text-on-gold)" }}>
        Create a free account
      </Link>
      <Link
        to="/login"
        search={{ next: back }}
        style={{ ...linkButton, border: "1px solid var(--border-strong)", color: "var(--text-primary)" }}
      >
        Sign in
      </Link>
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, textAlign: "center" }}>
        Joining a group is free.
      </p>
    </div>
  );
}

function SignedInCard({ code, inviter }: { code: string; inviter: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    clearPendingGroupJoin();
  }, []);

  const previewQuery = useQuery({
    queryKey: ["groups", "join", code],
    queryFn: () => fetchJoinPreview(code),
    retry: false,
  });

  const joinMutation = useMutation({
    mutationFn: () => joinGroup(code, inviter),
    onSuccess: async ({ groupId }) => {
      // An existing account earns no referral; don't leave one lying around.
      clearStoredReferral();
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      void navigate({ to: "/groups/$id", params: { id: groupId } });
    },
  });

  if (previewQuery.isLoading) {
    return (
      <div style={CARD_STYLE}>
        <Skel height="52px" />
        <Skel height="80px" />
        <Skel height="44px" />
      </div>
    );
  }

  if (previewQuery.isError) {
    const notFound = previewQuery.error instanceof ApiError && previewQuery.error.status === 404;
    return (
      <div style={CARD_STYLE}>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
          {notFound ? "This invite doesn't work any more" : "Couldn't open this invite"}
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
          {notFound
            ? "The code may have been changed by a group admin, or the group was deleted. Ask a member for a fresh link."
            : groupErrorMessage(previewQuery.error, "Try again in a moment.")}
        </p>
        <Link to="/groups" style={{ ...linkButton, border: "1px solid var(--border-strong)", color: "var(--text-primary)" }}>
          Go to your groups
        </Link>
      </div>
    );
  }

  const group = previewQuery.data!.group;

  return (
    <div style={CARD_STYLE}>
      <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <UsersRound size={12} /> Group invite
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <GroupImage name={group.name} imageUrl={group.imageUrl} size={60} />
        <div style={{ minWidth: 0 }}>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0, overflowWrap: "anywhere" }}>
            {group.name}
          </h1>
          <span className="eyebrow tnum" style={{ fontSize: 10 }}>
            {fmtNumber(group.memberCount)} member{group.memberCount === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      {group.description && (
        <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {group.description}
        </p>
      )}

      {group.alreadyMember ? (
        <>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0 }}>You're already in this group.</p>
          <Link
            to="/groups/$id"
            params={{ id: group.groupId }}
            style={{ ...linkButton, background: "var(--accent)", color: "var(--text-on-gold)" }}
          >
            Open the group
          </Link>
        </>
      ) : (
        <>
          <ConsentLine />
          {joinMutation.isError && (
            <ErrorLine>{groupErrorMessage(joinMutation.error, "Couldn't join the group. Try again.")}</ErrorLine>
          )}
          <Button size="lg" block disabled={joinMutation.isPending} onClick={() => joinMutation.mutate()}>
            {joinMutation.isPending ? "Joining…" : "Join group"}
          </Button>
          <Link to="/groups" style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
            Not now
          </Link>
        </>
      )}
    </div>
  );
}

export default function GroupJoinPage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const search = useSearch({ strict: false }) as { ref?: string };
  const inviter = search.ref?.trim() || null;
  const { user, isLoading } = useAuth();

  usePageMeta({
    title: "Group invite - Atlas",
    description: "You've been asked into a private group on Atlas.",
    canonicalPath: `/groups/join/${code}`,
    noindex: true,
  });

  return (
    <Frame>
      {isLoading ? (
        <div style={CARD_STYLE}>
          <Skel height="120px" />
        </div>
      ) : user ? (
        <SignedInCard code={code} inviter={inviter} />
      ) : (
        <SignedOutCard code={code} inviter={inviter} />
      )}
    </Frame>
  );
}
